<template>
  <div class="lot-navigation mb-2">
    <div v-if="showScrollButtons || searchQuery" class="search-container d-none d-md-block w-25">
      <b-input-group
        class="mr-md-4"
        size="sm">
        <b-input-group-prepend>
          <b-input-group-text class="search-input-prepend">
            <search-icon size="18"/>
          </b-input-group-text>
        </b-input-group-prepend>
        <b-form-input
          v-model="searchQuery"
          :class="{'active': searchQuery}"
          :placeholder="$t('Research')"
          class="search-input"
          type="search"
          @input="onSearchInput">
        </b-form-input>
      </b-input-group>
    </div>

    <div class="d-block d-md-none">
      <div v-if="lots.length > 5" class="search-container-mobile">
        <b-input-group
          class="mr-md-4"
          size="sm">
          <b-input-group-prepend>
            <b-input-group-text class="search-input-prepend">
              <search-icon size="18"/>
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            :class="{'active': searchQuery}"
            :placeholder="$t('Research')"
            class="search-input"
            type="search"
            @input="onSearchInput">
          </b-form-input>
        </b-input-group>
      </div>

      <b-dropdown
        :text="getCurrentLotLabel()"
        variant="outline-primary"
        class="w-100 lot-dropdown"
        menu-class="lot-dropdown-menu"
      >
        <b-dropdown-item
          v-if="showGeneralTab"
          :active="currentSection === generalTabIndex"
          @click="selectLot(generalTabIndex)"
          class="lot-dropdown-item general-tab-item"
        >
          <div class="lot-item-content">
            <div class="lot-number">{{ $t(generalTabLabel) }}</div>
          </div>
        </b-dropdown-item>

        <!-- Séparateur si général activé -->
        <b-dropdown-divider v-if="showGeneralTab"></b-dropdown-divider>

        <b-dropdown-item
          v-for="(lot, index) in filteredLots"
          :key="lot.originalIndex + '-dropdown-key'"
          :active="currentSection === lot.originalIndex"
          @click="selectLot(lot.originalIndex)"
          class="lot-dropdown-item"
        >
          <div class="lot-item-content">
            <div class="lot-number">{{ getLotNumber(lot.data, lot.originalIndex) }}</div>
            <div class="lot-name">{{ getLotName(lot.data, lot.originalIndex) }}</div>
          </div>
        </b-dropdown-item>
        <b-dropdown-item v-if="filteredLots.length === 0" disabled class="no-results">
          {{ $t('noLotsFound') }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="d-none d-md-block">
      <div class="lot-tabs-container">
        <div class="lot-tabs-scroll" ref="tabsContainer">
          <b-nav-item
            v-if="showGeneralTab"
            :class="{ 'link-active': currentSection === generalTabIndex }"
            class="lot-tab-item general-tab-item"
            ref="generalTab"
            @click.prevent="selectLot(generalTabIndex)"
          >
            <div class="lot-tab-content">
              <span class="general-tab-text">{{ $t(generalTabLabel) }}</span>
            </div>
          </b-nav-item>

          <b-nav-item
            v-for="(lot, index) in filteredLots"
            :key="lot.originalIndex + '-nav-key'"
            :class="{ 'link-active': currentSection === lot.originalIndex }"
            class="lot-tab-item"
            :ref="`lotTab-${lot.originalIndex}`"
            @click.prevent="selectLot(lot.originalIndex)"
          >
            <div class="lot-tab-content">
              <span class="lot-prefix">{{ lotPrefix }}</span>
              <span class="lot-number">{{ getLotNumber(lot.data, lot.originalIndex) }}</span>
              <span class="lot-separator"> - </span>
              <span class="lot-name" :title="getLotName(lot.data, lot.originalIndex)">
                {{ getLotName(lot.data, lot.originalIndex) }}
              </span>
            </div>
          </b-nav-item>
          <div v-if="filteredLots.length === 0 && !showGeneralTab" class="no-results-desktop">
            {{ $t('noLotsFound') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LotNavigation',
  props: {
    lots: {
      type: Array,
      required: true
    },
    currentSection: {
      type: Number,
      default: 0
    },
    lotPrefix: {
      type: String,
      default: 'OperationLot'
    },
    useValueProperty: {
      type: Boolean,
      default: true
    },
    showNewLabel: {
      type: Boolean,
      default: true
    },
    newLabel: {
      type: String,
      default: 'New'
    },
    operationLotLabel: {
      type: String,
      default: 'operationLot'
    },
    // Options pour l'onglet général
    showGeneralTab: {
      type: Boolean,
      default: false
    },
    generalTabLabel: {
      type: String,
      default: 'General'
    },
    generalTabIndex: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      showScrollButtons: false,
      hasScrollableContent: false,
      searchQuery: '',
      filteredLots: []
    };
  },
  mounted() {
    this.initializeFilteredLots();
    this.checkScrollButtons();
    this.checkInitialScrollableContent();
    window.addEventListener('resize', this.checkScrollButtons);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScrollButtons);
  },
  watch: {
    lots: {
      handler() {
        this.initializeFilteredLots();
        this.$nextTick(() => {
          this.checkScrollButtons();
          this.checkInitialScrollableContent();
        });
      },
      immediate: true
    },
    currentSection: {
      handler(newSection) {
        this.$nextTick(() => {
          this.scrollToActiveTab(newSection);
        });
      }
    }
  },
  methods: {
    initializeFilteredLots() {
      this.filteredLots = this.lots.map((lot, index) => ({
        data: lot,
        originalIndex: index
      }));
    },

    onSearchInput() {
      if (!this.searchQuery.trim()) {
        this.initializeFilteredLots();
      } else {
        const query = this.searchQuery.toLowerCase().trim();
        this.filteredLots = this.lots
          .map((lot, index) => ({
            data: lot,
            originalIndex: index
          })).filter(item => {
            const lotNumber = this.getLotNumber(item.data, item.originalIndex).toString().toLowerCase() || '';
            const lotName = this.getLotName(item.data, item.originalIndex).toString().toLowerCase() || '';
            const prefix = this.lotPrefix.toString().toLowerCase();
            const lotFullLabel = `${prefix} ${lotNumber} - ${lotName}`.trim();

            return (
              lotNumber.includes(query) ||
              lotName.includes(query) ||
              (`${prefix} ${lotNumber}`).includes(query) ||
              lotFullLabel.includes(query)
            );
          });

      }

      this.$nextTick(() => {
        this.checkScrollButtons();
      });
    },

    clearSearch() {
      this.searchQuery = '';
      this.initializeFilteredLots();
      this.$nextTick(() => {
        this.checkScrollButtons();
      });
    },

    selectLot(originalIndex) {
      this.$emit('update:currentSection', originalIndex);
      this.$emit('lot-selected', originalIndex);

      this.$nextTick(() => {
        this.scrollToActiveTab(originalIndex);
      });
    },

    scrollToActiveTab(activeIndex) {
      const container = this.$refs.tabsContainer;
      if (!container) return;

      let activeTab = null;

      // Trouver l'onglet actif
      if (this.showGeneralTab && activeIndex === this.generalTabIndex) {
        activeTab = this.$refs.generalTab.$el || this.$refs.generalTab;
      } else {
        const tabRef = this.$refs[`lotTab-${activeIndex}`];
        activeTab = Array.isArray(tabRef) ? tabRef[0].$el || tabRef[0] : tabRef.$el || tabRef;
      }

      if (!activeTab) return;

      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      // Calculer les positions relatives au container
      const tabLeft = tabRect.left - containerRect.left + container.scrollLeft;
      const tabRight = tabLeft + tabRect.width;
      const containerWidth = container.clientWidth;
      const currentScrollLeft = container.scrollLeft;

      // Vérifier si l'onglet est entièrement visible
      const isTabFullyVisible =
        tabLeft >= currentScrollLeft &&
        tabRight <= currentScrollLeft + containerWidth;

      if (!isTabFullyVisible) {
        let targetScrollLeft;

        // Si l'onglet déborde à droite
        if (tabRight > currentScrollLeft + containerWidth) {
          targetScrollLeft = tabRight - containerWidth + 20; // 20px de marge
        }
        // Si l'onglet déborde à gauche
        else if (tabLeft < currentScrollLeft) {
          targetScrollLeft = tabLeft - 20; // 20px de marge
        }

        // S'assurer que le scroll ne dépasse pas les limites
        targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, container.scrollWidth - containerWidth));

        // Effectuer le scroll avec animation
        container.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });

        // Mettre à jour les boutons de scroll après l'animation
        setTimeout(() => {
          this.checkScrollButtons();
        }, 300);
      }
    },

    getLotNumber(lot, index) {
      const hasValidLot = lot.lotCoPro && ((typeof lot.lotCoPro === 'object' && lot.lotCoPro.hasOwnProperty('value') && lot.lotCoPro.value) || (typeof lot.lotCoPro !== 'object' && lot.lotCoPro));
      return hasValidLot ? (this.useValueProperty ? lot.lotCoPro.value : lot.lotCoPro) : index + 1;
    },

    getLotName(lot, index) {
      const hasValidName = !!(lot.name && (
        (typeof lot.name === 'object' && lot.name.hasOwnProperty('value') && lot.name.value) ||
        (typeof lot.name !== 'object' && lot.name)
      ));
      return hasValidName ? (this.useValueProperty ? lot.name.value : lot.name) : (this.showNewLabel ? `${this.$t(this.newLabel)} ${this.$t(this.operationLotLabel)}` : '');
    },

    getCurrentLotLabel() {
      // Si c'est l'onglet général
      if (this.showGeneralTab && this.currentSection === this.generalTabIndex) {
        return this.$t(this.generalTabLabel);
      }

      const currentLot = this.lots[this.currentSection];
      if (!currentLot) return this.$t(this.lotPrefix);

      const number = this.getLotNumber(currentLot, this.currentSection);
      const name = this.getLotName(currentLot, this.currentSection);

      return `${this.$t(this.lotPrefix)} ${number}${name ? ' - ' + name : ''}`;
    },

    checkInitialScrollableContent() {
      this.$nextTick(() => {
        const container = this.$refs.tabsContainer;
        if (!container) return;

        // Sauvegarder temporairement la recherche
        const tempSearch = this.searchQuery;

        // Réinitialiser temporairement pour vérifier le contenu complet
        this.searchQuery = '';
        this.initializeFilteredLots();

        this.$nextTick(() => {
          this.hasScrollableContent = container.scrollWidth > container.clientWidth;

          // Restaurer la recherche et refiltrer
          this.searchQuery = tempSearch;
          if (this.searchQuery) {
            this.onSearchInput();
          }
        });
      });
    },

    checkScrollButtons() {
      this.$nextTick(() => {
        const container = this.$refs.tabsContainer;
        if (!container) return;

        this.showScrollButtons = container.scrollWidth > container.clientWidth;
        this.canScrollLeft = container.scrollLeft > 0;
        this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth);
      });
    },

    scrollLeft() {
      const container = this.$refs.tabsContainer;
      if (container) {
        container.scrollBy({ left: -200, behavior: 'smooth' });
        setTimeout(this.checkScrollButtons, 300);
      }
    },

    scrollRight() {
      const container = this.$refs.tabsContainer;
      if (container) {
        container.scrollBy({ left: 200, behavior: 'smooth' });
        setTimeout(this.checkScrollButtons, 300);
      }
    }
  }
};
</script>

<style scoped>
.lot-navigation {
  width: 100%;
  border-radius: 2px;
  background: white;
}

.general-tab-item .lot-item-content .lot-number {
  font-weight: bold;
  color: var(--main-color-text, #495057);
  font-size: 1em;
}

.general-tab-text {
  font-weight: bold;
  color: var(--main-color-text, #495057);
}

/* Style pour "aucun résultat" */
.no-results {
  text-align: center;
  color: var(--main-color-text, #6c757d);
  font-style: italic;
  padding: 12px;
}

.no-results-desktop {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: var(--main-color-text, #6c757d);
  font-style: italic;
  min-width: 200px;
}

/* Dropdown mobile */
.lot-dropdown {
  width: 100%;
  margin: 10px;
}

.lot-dropdown .btn {
  font-weight: bold;
  font-size: 14px;
}

.lot-dropdown-menu {
  max-height: 300px;
  overflow-y: auto;
  width: 100%;
}

.lot-dropdown-item {
  white-space: normal;
  padding: 8px 16px;
  font-weight: bold;
  font-size: 14px;
}

.lot-dropdown-item.active {
  background-color: var(--main-red);
  color: white;
}

.lot-item-content {
  display: flex;
  flex-direction: column;
}

.lot-number {
  font-weight: bold;
}

.lot-name {
  font-size: 0.9em;
  margin-top: 2px;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lot-tabs-container {
  position: relative;
  display: flex;
  align-items: stretch;
  max-width: 100%;

}

.lot-tabs-scroll {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  flex: 1;
  height: auto;
  max-width: 100%;
}

.lot-tabs-scroll::-webkit-scrollbar {
  height: 12px;
  border-radius: 8px;
  background: var(--background-grey);
}

.lot-tabs-scroll::-webkit-scrollbar-button {
  width: 0;
  height: 0;
}

.lot-tabs-scroll::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: var(--main-color);

}

.lot-tab-item {
  font-weight: bold;
  font-size: 14px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 4px 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 250px;
  background: transparent;
  color: inherit;
  text-decoration: none;
  position: relative;
  border: none;

}

.lot-tab-item.link-active {
  color: var(--main-color-text);
}

.lot-tabs-container {
  position: relative;
}

.lot-tabs-container::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: var(--main-grey);
  z-index: 1;
}

.lot-tab-item .nav-link {
  position: relative;
  display: inline-block;
  padding-bottom: 8px;
  z-index: 2;
}

.lot-tab-item.link-active .nav-link::after {
  content: "";
  position: absolute;
  bottom: -4px;
  right: 0;
  width: 100%;
  height: 3px;
  background-color: var(--main-red);
  z-index: 3;
}

.lot-tab-item:first-child.link-active .nav-link::after {
  content: "";
  position: absolute;
  bottom: -4px;
  right: -3px;
  width: 110%;
  height: 3px;
  background-color: var(--main-red);
  z-index: 3;
}



.lot-tab-item.link-active .nav-link {
  color: var(--main-color-text);
}

.lot-tab-content {
  color: var(--main-color-text);
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
}

.lot-tab-content:hover, .general-tab-text:hover {
  color: var(--main-color-hover);
}

.lot-prefix {
  font-weight: bold;
  margin-right: 4px;
}

.lot-number {
  font-weight: bold;
}

.lot-separator {
  margin: 0 4px;
}

.lot-name {
  font-weight: bold;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .lot-dropdown .btn {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lot-navigation {
    border: none;
    padding: 0;
  }

  .search-container-mobile {
    margin: 0 10px;
    padding: 8px 0;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .lot-tab-item {
    max-width: 200px;
    padding: 6px 10px;
  }

  .lot-name {
    max-width: 100px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/LotNavigation.vue